import React, { useContext, useEffect, useMemo, useState } from 'react';
import { authContext } from '../../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useApiData from "../../services/useApiData.js"
import SnackbarEx from '../Helpers/SnackbarEx.jsx';
import GridRowContextMenu, {
    buildUserShareUrl,
    copyShareUrlAction,
    useGridRowContextMenu,
} from '../Helpers/GridRowContextMenu.jsx';
import ConfirmDeleteDialog from '../Helpers/ConfirmDeleteDialog.jsx';
import { DataGrid } from "@mui/x-data-grid"
import { Button, Drawer, CircularProgress } from '@mui/material';
import AddUser from './AddUser.jsx';
import DeleteData from '../../services/DeleteData.js';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {
    appDataGridSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
    selectionIds,
} from '../Helpers/dataGridStyles.js';
import GridFilterBar, { useGridFilter } from '../Helpers/GridFilterBar.jsx';
import GridLayoutControls from '../Helpers/GridLayoutControls.jsx';
import useGridLayout from '../Helpers/useGridLayout.js';
import { GRID_LAYOUT_KEYS } from '../Helpers/gridLayoutKeys.js';
import { USER_FILTER_FIELDS } from '../Helpers/gridFilterFields.js';
import {
    catalogDangerButtonSx,
    catalogPrimaryButtonSx,
} from '../Products/catalogGridStyles.js';
import '../Products/catalog.css';

export default function UserBase() {
    const { userRole } = useContext(authContext)
    const [searchParams] = useSearchParams();
    const { data: userAccounts, error: accountError, isLoading: loadAccounts, refetch: refetchAccounts } = useApiData("api/all-user-accounts/no3retailers");
    const { apply: applyFilter, barProps: filterBar } = useGridFilter(USER_FILTER_FIELDS);

    const [openAddUser, setOpenAddUser] = useState(false)
    const [row, setRow] = useState([])
    const [selectionModel, setSelectionModel] = useState({
        type: "include",
        ids: new Set(),
    });
    const [col, setCol] = useState([])
    const [selectedEvent, setSelectedEvent] = useState([])
    const [enableDelete, setEnableDelete] = useState(false)
    const [deleteLoad, setDeleteLoad] = useState(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [openTestSnack, setOpenTestSnack] = useState({
        color: "",
        message: "",
        open: false
    })
    const {
        menu: rowMenu,
        close: closeRowMenu,
        rowContextMenuSlotProps,
    } = useGridRowContextMenu();
    const layout = useGridLayout(GRID_LAYOUT_KEYS.userBase, {
        sortModel: [],
        columnVisibilityModel: {},
    });
    const columns = useMemo(
        () => layout.applyWidths(col),
        [col, layout.draftWidths, layout.applyWidths]
    );

    useEffect(() => {
        setData()
    }, [userAccounts])

    const urlQ = (searchParams.get("q") || "").trim().toLowerCase();
    const filteredRows = useMemo(() => {
        let next = applyFilter(row);
        if (urlQ) {
            next = next.filter((r) =>
                String(r?.email || "")
                    .toLowerCase()
                    .includes(urlQ)
            );
        }
        return next;
    }, [row, applyFilter, urlQ]);

    const rowMenuActions = useMemo(
        () => [
            copyShareUrlAction({
                buildUrl: (r) => buildUserShareUrl(r),
                disabled: (r) => !String(r?.email || "").trim(),
                onCopied: () =>
                    setOpenTestSnack({
                        color: "success",
                        message: "Link copied.",
                        open: true,
                    }),
            }),
        ],
        []
    );
    const gridSlotProps = useMemo(
        () => rowContextMenuSlotProps(filteredRows, appDataGridSlotProps),
        [rowContextMenuSlotProps, filteredRows]
    );

    const setData = () => {
        setRow(userAccounts?.map((e, ind) =>
        ({
            id: ind,
            email: e?.email,
            firstName: e.fName,
            lastName: e.lName
        })
        ) ?? [])

        setCol([
            {
                field: 'email',
                headerName: 'User Email',
                minWidth: 300
            },
            {
                field: 'firstName',
                headerName: 'First Name',
                minWidth: 300
            },
            {
                field: 'lastName',
                headerName: 'Last Name',
                minWidth: 300
            },
        ]);
    }

    const deleteTargets = useMemo(() => {
        const list = Array.isArray(selectedEvent) ? selectedEvent : [];
        return list.map((user, index) => ({
            key: String(user?._id || user?.id || index),
            label: [user?.fName || user?.firstName, user?.lName || user?.lastName, user?.email]
                .filter(Boolean)
                .join(" ") || String(user?._id || user?.id || `User ${index + 1}`),
        }));
    }, [selectedEvent]);

    const deleteUsers = async () => {
        setDeleteLoad(true)
        const { success: deleteSuccess } = await DeleteData("delete-user", selectedEvent)
        setDeleteLoad(false)
        if (deleteSuccess) {
            setDeleteDialogOpen(false)
            setOpenTestSnack({ color: "success", message: "Account deleted successully.", open: true })
            setTimeout(() => {
                refetchAccounts()
                setSelectedEvent(false)
                setEnableDelete(false)
                setSelectionModel({ type: "include", ids: new Set() })
            }, 200)
        } else {
            setOpenTestSnack({ color: "error", message: "Delete failed.", open: true })
        }
    }

    const selectClick = (e) => {
        const ids = selectionIds(e);
        if (ids.length > 0) {
            setEnableDelete(true)
            // Map all selected IDs to their Parser Item ID values
            const selectedRecords = ids
                .map(index => userAccounts[index]); // Get records by index                        
            setSelectedEvent(prevSelectedEvent => [...(prevSelectedEvent || []), ...selectedRecords]);

        } else {
            setEnableDelete(false)
            setSelectedEvent(false)
        }
    }

    return (
        <>
            <div className="catalog-grid-shell">
                <div className="catalog-toolbar">
                    <div className="catalog-toolbar-spacer" />
                    <GridLayoutControls layout={layout} columns={col} />
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!enableDelete || deleteLoad}
                        startIcon={
                            deleteLoad ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : (
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                            )
                        }
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={catalogDangerButtonSx}
                    >
                        Delete
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<PersonAddAltOutlinedIcon fontSize="small" />}
                        onClick={() => setOpenAddUser(true)}
                        sx={{ ...catalogPrimaryButtonSx, whiteSpace: "nowrap" }}
                    >
                        Add User
                    </Button>
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={loadAccounts}
                        checkboxSelection
                        disableRowSelectionOnClick
                        onRowSelectionModelChange={(newSelectionModel) => {
                            setSelectionModel(newSelectionModel);
                            selectClick(newSelectionModel);
                        }}
                        rowSelectionModel={selectionModel}
                        rows={filteredRows}
                        columns={columns}
                        sortModel={layout.sortModel}
                        onSortModelChange={layout.onSortModelChange}
                        columnVisibilityModel={layout.columnVisibilityModel}
                        onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                        onColumnWidthChange={layout.onColumnWidthChange}
                        pageSizeOptions={[10, 25]}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 25,
                                },
                            },
                        }}
                        slotProps={gridSlotProps}
                        sx={appDataGridSx}
                    />
                </div>
            </div>
            <GridRowContextMenu
                menu={rowMenu}
                onClose={closeRowMenu}
                actions={rowMenuActions}
            />
            <Drawer
                anchor='right'
                sx={{
                    '& .MuiDrawer-paper': {
                        height: '100%',
                        width: '40%',
                        borderRadius: '10px',
                        flexDirection: 'row',
                        overflow: 'hidden'
                    }
                }}
                open={openAddUser}
                onClose={(val) => {
                    setOpenAddUser(false)
                    if (val === 'success') { refetchAccounts() }
                }}
            >

                <AddUser close={(val) => {
                    setOpenAddUser(false)
                    if (val === 'success') { refetchAccounts() }

                }} />
            </Drawer >
            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={deleteUsers}
                confirming={deleteLoad}
                title={
                    deleteTargets.length === 1
                        ? "Delete user?"
                        : `Delete ${deleteTargets.length} users?`
                }
                description="This cannot be undone. The following accounts will be removed:"
                items={deleteTargets}
            />
            {
                openTestSnack.open && <SnackbarEx
                    color={openTestSnack.color}
                    message={openTestSnack.message}
                    open={openTestSnack.open}
                    close={() => setOpenTestSnack({
                        color: "",
                        message: "",
                        open: false
                    })} />
            }
        </>
    );
};